/**
 * SalesModalRoute Component
 *
 * Route-integrated wrapper for the SalesModal component that enables modal behavior
 * through URL navigation. This component demonstrates the system's pattern for
 * integrating modals with React Router.
 *
 * ## Key Features
 *
 * ### URL State Management
 * - Modal state is synchronized with the URL
 * - Opening modal navigates to `/services/sales/new`
 * - Closing modal navigates back to `/services/sales`
 * - Supports query parameters for pre-filling form data
 *
 * ### Deep Linking Support
 * - Direct navigation to `/services/sales/new` opens the modal
 * - Query parameters populate initial form data
 * - Example: `/services/sales/new?client_id=123&type=budget`
 *
 * ### Browser Integration
 * - Back button closes the modal
 * - Forward button reopens the modal
 * - Browser history is properly managed
 * - Modal state persists across page refreshes
 *
 * ### Modal Stack Management
 * - Integrates with the modal stack system
 * - Prevents multiple modals from conflicting
 * - Ensures proper z-index layering
 *
 * ## Usage Pattern
 *
 * This component should be used as a Route element in your routing configuration:
 *
 * ```tsx
 * // In App.tsx or route configuration
 * import { SalesModalRoute } from '@/components/modals/services/SalesModalRoute';
 *
 * <Routes>
 *   <Route path="/services/sales" element={<SalesListPage />}>
 *     <Route path="new" element={<SalesModalRoute />} />
 *     <Route path=":id" element={<SaleDetailPage />} />
 *   </Route>
 * </Routes>
 * ```
 *
 * ## Programmatic Usage
 *
 * Use the `useSalesModal` hook for programmatic control:
 *
 * ```tsx
 * import { useSalesModal } from '@/components/modals/services/SalesModalRoute';
 *
 * function MyComponent() {
 *   const { openSalesModal, closeSalesModal, isOpen } = useSalesModal();
 *
 *   const handleNewSale = () => {
 *     openSalesModal({
 *       mode: 'sale',
 *       clientId: 'client-123',
 *       saleDate: '2024-12-02'
 *     });
 *   };
 *
 *   return (
 *     <button onClick={handleNewSale}>
 *       New Sale
 *     </button>
 *   );
 * }
 * ```
 *
 * ## URL Parameters
 *
 * The following query parameters are supported:
 * - `type`: 'sale' | 'budget' - Form type
 * - `client_id`: string - Pre-select client
 * - `cost_center_id`: string - Pre-select cost center
 * - `salesperson_id`: string - Pre-select salesperson
 * - `sale_date`: string (ISO date) - Pre-fill sale date
 * - `expiry_date`: string (ISO date) - Pre-fill expiry date
 *
 * @see {@link SalesModal} for the modal component
 * @see {@link useSalesModal} for programmatic control
 */


import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SalesModal } from './SalesModal';
import { useModalStack } from '../hooks/useModal';
import { toast } from 'sonner';
import { useServiceSale } from '@/hooks/useServiceSales';
import { serviceSaleSchema } from './SalesModal';

/**
 * Props for the SalesModalRoute component
 * 
 * These props allow customization of the route-modal integration behavior.
 * Most applications will use the default values.
 */
interface SalesModalRouteProps {
  /** 
   * Base path for the sales routes
   * Used to construct navigation URLs
   * @default '/services/sales'
   */
  basePath?: string;

  /** 
   * Default mode for the modal when no type is specified in URL
   * @default 'auto'
   */
  mode?: 'sale' | 'budget' | 'auto';

  /** 
   * Callback fired when a sale is successfully created
   * @param saleId - The ID of the newly created sale
   */
  onSuccess?: (saleId: string) => void;

  /** 
   * Callback fired when an error occurs
   * @param error - The error that occurred
   */
  onError?: (error: Error) => void;

  /** 
   * Initial data to pre-populate the form
   * Merged with URL parameters (URL parameters take precedence)
   */
  initialData?: Partial<z.infer<typeof serviceSaleSchema>>;
}

/**
 * SalesModalRoute Component
 *
 * Integrates the SalesModal with React Router by:
 * - Monitoring route changes
 * - Managing URL state
 * - Handling browser back navigation
 * - Providing deep linking support
 */
export const SalesModalRoute: React.FC<SalesModalRouteProps> = ({
  basePath = '/services/sales',
  mode = 'auto',
  onSuccess,
  onError,
  initialData,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { isTopModal, pushModal, popModal } = useModalStack();

  // Local state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'sale' | 'budget' | 'auto'>(mode);

  // Determine if we're in edit mode from the URL
  const routeInfo = useMemo(() => {
    const path = location.pathname;
    const editMatch = path.match(new RegExp(`^${basePath}/([^/]+)/edit$`));
    const isNewRoute = path === `${basePath}/new`;
    const isBudgetRoute = path === `${basePath}/new/budget`;
    const isSaleRoute = path === `${basePath}/new/sale`;

    return {
      isEditMode: !!editMatch,
      saleId: editMatch ? editMatch[1] : null,
      isNewRoute,
      isBudgetRoute,
      isSaleRoute,
    };
  }, [location.pathname, basePath]);

  // Fetch sale data when in edit mode
  const { data: saleData, isLoading: isLoadingSale } = useServiceSale(routeInfo.saleId);

  // Check if current route should show the modal
  const shouldShowModal = useMemo(() => {
    if (routeInfo.isEditMode) {
      // For edit mode, show modal once data is loaded
      return !isLoadingSale;
    }

    if (routeInfo.isBudgetRoute) {
      setModalMode('budget');
      return true;
    }

    if (routeInfo.isSaleRoute) {
      setModalMode('sale');
      return true;
    }

    if (routeInfo.isNewRoute) {
      setModalMode(mode);
      return true;
    }

    return false;
  }, [routeInfo, isLoadingSale, mode]);

  // Extract initial data from URL search params
  const getInitialDataFromURL = React.useCallback((): Partial<z.infer<typeof serviceSaleSchema>> => {
    const searchParams = new URLSearchParams(location.search);
    const urlData: Partial<z.infer<typeof serviceSaleSchema>> = {};

    // Extract type from URL
    const urlType = searchParams.get('type');
    if (urlType === 'budget' || urlType === 'sale') {
      urlData.type = urlType;
    }

    // Extract client_id
    const clientId = searchParams.get('client_id');
    if (clientId) {
      urlData.client_id = clientId;
    }

    // Extract cost_center_id
    const costCenterId = searchParams.get('cost_center_id');
    if (costCenterId) {
      urlData.cost_center_id = costCenterId;
    }

    // Extract salesperson_id
    const salespersonId = searchParams.get('salesperson_id');
    if (salespersonId) {
      urlData.salesperson_id = salespersonId;
    }

    // Extract dates
    const saleDate = searchParams.get('sale_date');
    if (saleDate) {
      urlData.sale_date = saleDate;
    }

    const expiryDate = searchParams.get('expiry_date');
    if (expiryDate) {
      urlData.expiry_date = expiryDate;
    }

    return { ...urlData, ...initialData };
  }, [location.search, initialData]);

  // Convert sale data to form initial data
  const getSaleDataAsFormData = React.useCallback((): Partial<z.infer<typeof serviceSaleSchema>> => {
    if (!saleData) return {};

    return {
      type: saleData.type,
      client_id: saleData.client_id || undefined,
      sale_date: saleData.sale_date || new Date().toISOString().split('T')[0],
      expiry_date: saleData.expiry_date || undefined,
      cost_center_id: saleData.cost_center_id ? String(saleData.cost_center_id) : undefined,
      salesperson_id: saleData.salesperson_id || undefined,
      notes: saleData.notes || undefined,
      terms_conditions: saleData.terms_conditions || undefined,
      client_notes: saleData.client_notes || undefined,
      internal_notes: saleData.internal_notes || undefined,
    };
  }, [saleData]);

  // Get the initial data for the form
  const getInitialData = React.useCallback((): Partial<z.infer<typeof serviceSaleSchema>> => {
    if (routeInfo.isEditMode) {
      return getSaleDataAsFormData();
    }
    return getInitialDataFromURL();
  }, [routeInfo.isEditMode, getSaleDataAsFormData, getInitialDataFromURL]);

  // Handle modal open/close
  useEffect(() => {
    if (shouldShowModal && !isModalOpen) {
      setIsModalOpen(true);
      pushModal('sales-modal');
    } else if (!shouldShowModal && isModalOpen) {
      setIsModalOpen(false);
      popModal();
    }
  }, [shouldShowModal, isModalOpen, pushModal, popModal]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isModalOpen && !shouldShowModal) {
        setIsModalOpen(false);
        popModal();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isModalOpen, shouldShowModal, popModal]);

  // Handle success callback
  const handleSuccess = (saleId: string) => {
    // Navigate to the sale detail page or list page
    navigate(`${basePath}/${saleId}`, { replace: true });
    onSuccess?.(saleId);
  };

  // Handle error callback
  const handleError = (error: Error) => {
    console.error('SalesModal error:', error);
    onError?.(error);

    // Don't close modal on error, let user try again
    toast.error(error.message || 'Ocorreu um erro. Tente novamente.');
  };

  // Handle modal close
  const handleModalClose = (open: boolean) => {
    if (!open) {
      // Navigate back to the base path
      navigate(basePath, { replace: true });
    }
    setIsModalOpen(open);
  };

  // Don't render anything if modal shouldn't be shown
  if (!shouldShowModal && !isModalOpen) {
    return null;
  }

  return (
    <SalesModal
      open={isModalOpen}
      onOpenChange={handleModalClose}
      mode={modalMode}
      initialData={getInitialData()}
      onSuccess={handleSuccess}
      saleId={routeInfo.saleId}
    />
  );
};

SalesModalRoute.displayName = 'SalesModalRoute';

/**
 * Hook for programmatically controlling the sales modal
 *
 * Provides functions to open/close the sales modal and check its state.
 * The modal is controlled through URL navigation, ensuring browser history
 * and deep linking work correctly.
 *
 * @example
 * ```tsx
 * function SalesListPage() {
 *   const { openSalesModal, closeSalesModal, isOpen } = useSalesModal();
 *
 *   const handleNewSale = () => {
 *     openSalesModal({ mode: 'sale' });
 *   };
 *
 *   const handleNewBudget = () => {
 *     openSalesModal({
 *       mode: 'budget',
 *       clientId: selectedClient?.id,
 *       saleDate: new Date().toISOString().split('T')[0]
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleNewSale}>New Sale</button>
 *       <button onClick={handleNewBudget}>New Budget</button>
 *       {isOpen && <p>Modal is currently open</p>}
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns Object with modal control functions and state
 */
export function useSalesModal() {
  const navigate = useNavigate();
  const { isTopModal } = useModalStack();

  const openSalesModal = React.useCallback((options?: {
    mode?: 'sale' | 'budget' | 'auto';
    clientId?: string;
    costCenterId?: string;
    salespersonId?: string;
    saleDate?: string;
    expiryDate?: string;
    replace?: boolean;
  }) => {
    const {
      mode = 'auto',
      clientId,
      costCenterId,
      salespersonId,
      saleDate,
      expiryDate,
      replace = false,
    } = options || {};

    // Build search params
    const searchParams = new URLSearchParams();

    if (mode !== 'auto') {
      searchParams.set('type', mode);
    }
    if (clientId) searchParams.set('client_id', clientId);
    if (costCenterId) searchParams.set('cost_center_id', costCenterId);
    if (salespersonId) searchParams.set('salesperson_id', salespersonId);
    if (saleDate) searchParams.set('sale_date', saleDate);
    if (expiryDate) searchParams.set('expiry_date', expiryDate);

    const queryString = searchParams.toString();
    const url = queryString
      ? `/services/sales/new?${queryString}`
      : `/services/sales/new`;

    navigate(url, { replace });
  }, [navigate]);

  const closeSalesModal = React.useCallback(() => {
    navigate('/services/sales', { replace: true });
  }, [navigate]);

  const isOpen = isTopModal('sales-modal');

  return {
    openSalesModal,
    closeSalesModal,
    isOpen,
  };
}

/**
 * Utility function to generate a URL for opening the sales modal
 *
 * Useful for creating links or programmatic navigation without using the hook.
 * Generates a properly formatted URL with query parameters.
 *
 * @example
 * ```tsx
 * // Generate URL for a new sale with pre-filled client
 * const url = generateSalesModalURL({
 *   mode: 'sale',
 *   clientId: 'client-123'
 * });
 * // Returns: '/services/sales/new?type=sale&client_id=client-123'
 *
 * // Use in a Link component
 * <Link to={generateSalesModalURL({ mode: 'budget' })}>
 *   Create Budget
 * </Link>
 * ```
 *
 * @param options - Configuration for the modal URL
 * @returns Formatted URL string with query parameters
 */
export function generateSalesModalURL(options?: {
  mode?: 'sale' | 'budget' | 'auto';
  clientId?: string;
  costCenterId?: string;
  salespersonId?: string;
  saleDate?: string;
  expiryDate?: string;
}): string {
  const {
    mode = 'auto',
    clientId,
    costCenterId,
    salespersonId,
    saleDate,
    expiryDate,
  } = options || {};

  const searchParams = new URLSearchParams();

  if (mode !== 'auto') {
    searchParams.set('type', mode);
  }
  if (clientId) searchParams.set('client_id', clientId);
  if (costCenterId) searchParams.set('cost_center_id', costCenterId);
  if (salespersonId) searchParams.set('salesperson_id', salespersonId);
  if (saleDate) searchParams.set('sale_date', saleDate);
  if (expiryDate) searchParams.set('expiry_date', expiryDate);

  const queryString = searchParams.toString();
  return queryString
    ? `/services/sales/new?${queryString}`
    : `/services/sales/new`;
}

export type { SalesModalRouteProps };